import type { StaticImageData } from 'next/image';
import type { STATUS_REWARD_BENEFIT_IDS } from '@/components/entities/gamification/GmAwardCard/utils/constants';
import type { GmRewardAction, GmRewardPerk, GmStatusKey } from '@/components/features/gamification/model/types/gm-statuses';
import type { RewardTypeValue } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import { statusKeys } from '@/components/features/gamification/model/constants/statuses';
import BIRTHDAY_BONUS from '@/public/assets/image/gamification/perks/birthday-bonus@3x.webp';
import EXCLUSIVE_OFFERS from '@/public/assets/image/gamification/perks/exclusive-offers@3x.webp';
import FREE_SPINS from '@/public/assets/image/gamification/perks/free-spins@3x.webp';
import HIGHER_DAILY_REDEMPTION from '@/public/assets/image/gamification/perks/higher-daily-redemption@3x.webp';
import LEVEL_UP_SCRATCH_CARD from '@/public/assets/image/gamification/perks/level-up-scratch-card@3x.webp';
import LOSS_INSURANCE from '@/public/assets/image/gamification/perks/loss-insurance@3x.webp';
import MONTHLY_PLAYBACK from '@/public/assets/image/gamification/perks/monthly-playback@3x.webp';
import PERSONAL_CONCIERGE from '@/public/assets/image/gamification/perks/personal-concierge@3x.webp';
import PERSONAL_SLOT_GAME from '@/public/assets/image/gamification/perks/personal-slot-game@3x.webp';

import PRIORITY_REDEMPTIONS from '@/public/assets/image/gamification/perks/priority-redemptions@3x.webp';
import PURCHASE_BONUS from '@/public/assets/image/gamification/perks/purchase-bonus@3x.webp';
import REFERRAL from '@/public/assets/image/gamification/perks/referral@3x.webp';
import STATUS_UP_SCRATCH_CARD from '@/public/assets/image/gamification/perks/status-up-scratch-card@3x.webp';
import WEEKLY_PLAYBACK from '@/public/assets/image/gamification/perks/weekly-playback@3x.webp';

import WINNERS_POOL from '@/public/assets/image/gamification/perks/winners-pool@3x.webp';
import { GamificationRewardType } from '@/queries/playerGamificationRewards/playerGamificationRewards.schema';
import messages from '@/translation/en.json';

const { titles } = messages.gamification.perks;

const getAllStatusValues = (value: string): Record<GmStatusKey, string> => {
  return Object.fromEntries(statusKeys.map(statusKey => [statusKey, value])) as Record<GmStatusKey, string>;
};

export interface StatusRewardBenefit {
  id: (typeof STATUS_REWARD_BENEFIT_IDS)[number];
  img: StaticImageData;
  type?: RewardTypeValue;
  title: string;
  values: Partial<Record<GmStatusKey, string>>;
  action?: GmRewardAction;
  link?: string;
  /**
   * Benefit is live and unlocks from the lowest status listed in `values`.
   * Benefits with a backend `type` are locked by grouped rewards instead; the rest are "Coming soon".
   */
  enabled?: boolean;
}

export type StatusRewardTableBenefit = Pick<StatusRewardBenefit, 'id' | 'action' | 'img' | 'title' | 'values' | 'link' | 'type' | 'enabled'>;

export type StatusRewardPerk = GmRewardPerk & Pick<StatusRewardBenefit, 'values' | 'enabled'>;

export const STATUS_REWARD_BENEFITS = [
  {
    id: 'loss-insurance',
    img: LOSS_INSURANCE,
    title: titles['loss-insurance'],
    // link: '/gamification/loss-insurance',
    values: {
      starter: 'up to 2.5%',
      bronze: 'up to 5%',
      silver: 'up to 10%',
      gold: 'up to 25%',
      platinum: 'up to 100%',
      diamond: 'up to 250%',
      master: 'up to 250%',
      grandmaster: 'up to 250%',
      champion: 'up to 250%',
      legend: 'up to 500%',
    },
  },
  {
    id: 'referral',
    img: REFERRAL,
    title: titles.referral,
    // link: '/gamification/loss-insurance',
    values: getAllStatusValues('up to 3500 SC'),
  },
  {
    id: 'winners-pool',
    img: WINNERS_POOL,
    title: titles['winners-pool'],
    // link: '/gamification/loss-insurance',
    values: getAllStatusValues('up to 150k SC'),
  },
  {
    id: 'level-up-scratch-card',
    img: LEVEL_UP_SCRATCH_CARD,
    type: GamificationRewardType.LevelUp,
    title: titles['level-up-scratch-card'],
    link: '/gamification/level-up',
    action: 'level-up-card',
    values: {
      starter: 'up to 0.5 SC',
      bronze: 'up to 1 SC',
      silver: 'up to 2.5 SC',
      gold: 'up to 5 SC',
      platinum: 'up to 7.5 SC',
      diamond: 'up to 10 SC',
      master: 'up to 15 SC',
      grandmaster: 'up to 20 SC',
      champion: 'up to 50 SC',
      legend: 'up to 1000 SC',
    },
  },
  {
    id: 'higher-daily-redemption',
    enabled: true,
    img: HIGHER_DAILY_REDEMPTION,
    title: titles['higher-daily-redemption'],
    values: {
      starter: 'up to 100 SC / 24 h',
      bronze: 'up to 200 SC / 24 h',
      silver: 'up to 500 SC / 24 h',
      gold: 'up to 1000 SC / 24 h',
      platinum: 'up to 1500 SC / 24 h',
      diamond: 'up to 2500 SC / 24 h',
      master: 'up to 5000 SC / 24 h',
      grandmaster: 'up to 10000 SC / 24 h',
      champion: 'up to 20000 SC / 24 h',
      legend: 'Custom / 24h',
    },
  },
  {
    id: 'status-up-scratch-card',
    img: STATUS_UP_SCRATCH_CARD,
    type: GamificationRewardType.StatusUp,
    title: titles['status-up-scratch-card'],
    action: 'status-up-card',
    link: '/gamification/status-up',
    values: {
      bronze: 'up to 2 SC',
      silver: 'up to 5 SC',
      gold: 'up to 10 SC',
      platinum: 'up to 20 SC',
      diamond: 'up to 30 SC',
      master: 'up to 50 SC',
      grandmaster: 'up to 75 SC',
      champion: 'up to 100 SC',
      legend: 'up to 150 SC',
    },
  },
  {
    id: 'free-spins',
    img: FREE_SPINS,
    title: titles['free-spins'],
    values: {
      silver: '10 spins × 0.20 SC',
      gold: '20 spins × 0.20 SC',
      platinum: '30 spins × 0.20 SC',
      diamond: '50 spins × 0.20 SC',
      master: '70 spins × 0.20 SC',
      grandmaster: '100 spins × 0.20 SC',
      champion: '150 spins × 0.20 SC',
      legend: '200 spins × 0.20 SC',
    },
  },
  {
    id: 'weekly-playback',
    img: WEEKLY_PLAYBACK,
    title: titles['weekly-playback'],
    values: {
      silver: '1%',
      gold: '1%',
      platinum: '1%',
      diamond: '1.2%',
      master: '1.5%',
      grandmaster: '1.5%',
      champion: '2%',
      legend: '2.5%',
    },
  },
  {
    id: 'priority-redemptions',
    enabled: true,
    img: PRIORITY_REDEMPTIONS,
    title: titles['priority-redemptions'],
    values: {
      gold: 'Processed first',
      platinum: 'Processed first',
      diamond: 'Processed first',
      master: 'Processed first',
      grandmaster: 'Processed first',
      champion: 'Processed first',
      legend: 'Processed first',
    },
  },
  {
    id: 'monthly-playback',
    img: MONTHLY_PLAYBACK,
    title: titles['monthly-playback'],
    values: {
      gold: '1%',
      platinum: '1.5%',
      diamond: '2%',
      master: '2.5%',
      grandmaster: '3%',
      champion: '4%',
      legend: '5%',
    },
  },
  {
    id: 'birthday-bonus',
    img: BIRTHDAY_BONUS,
    title: titles['birthday-bonus'],
    values: {
      gold: 'up to 100 SC',
      platinum: 'up to 250 SC',
      diamond: 'up to 500 SC',
      master: 'up to 1000 SC',
      grandmaster: 'up to 2500 SC',
      champion: 'up to 5000 SC',
      legend: 'up to 10000 SC',
    },
  },
  {
    id: 'personal-concierge',
    enabled: true,
    action: 'open-support',
    img: PERSONAL_CONCIERGE,
    title: titles['personal-concierge'],
    values: {
      platinum: 'Tailored Service',
      diamond: 'Tailored Service',
      master: 'Tailored Service',
      grandmaster: 'Tailored Service',
      champion: 'Tailored Service',
      legend: 'Tailored Service',
    },
  },
  {
    id: 'exclusive-offers',
    enabled: true,
    action: 'open-support',
    img: EXCLUSIVE_OFFERS,
    title: titles['exclusive-offers'],
    values: {
      platinum: 'Made for you',
      diamond: 'Made for you',
      master: 'Made for you',
      grandmaster: 'Made for you',
      champion: 'Made for you',
      legend: 'Made for you',
    },
  },
  {
    id: 'purchase-bonus',
    img: PURCHASE_BONUS,
    title: titles['purchase-bonus'],
    values: {
      platinum: '50%',
      diamond: '100%',
      master: '150%',
      grandmaster: '250%',
      champion: '500%',
      legend: '1,000%',
    },
  },
  {
    id: 'personal-slot-game',
    img: PERSONAL_SLOT_GAME,
    title: titles['personal-slot-game'],
    values: {
      grandmaster: 'Crafted for you',
      champion: 'Crafted for you',
      legend: 'Crafted for you',
    },
  },
] satisfies StatusRewardBenefit[];

export const getStatusRewardTableBenefits = (): StatusRewardTableBenefit[] => {
  return STATUS_REWARD_BENEFITS.map(({ id, action, img, title, values, link, type, enabled }) => ({
    id,
    action,
    img,
    title,
    type,
    values,
    link,
    enabled,
  }));
};

export const getStatusRewardPerks = (statusKey?: GmStatusKey): StatusRewardPerk[] => {
  if (!statusKey) {
    return [];
  }

  return STATUS_REWARD_BENEFITS.map(({ id, action, img, type, title, values, link, enabled }) => {
    const subTitle = values[statusKey];

    return {
      id,
      action,
      img,
      type,
      title,
      subTitle: subTitle ?? '',
      isDisabled: !subTitle,
      link,
      values,
      enabled,
    };
  });
};
